import { ProductsApiService } from '../../../../service/website/productsApiService';
import { PRICE_LOGIC } from '../../../../helper/constants';

// Normalize remote media URLs to https and strip protocol-relative forms
export const sanitizeUrl = u => {
  if (!u || typeof u !== 'string') return '';
  let url = u.trim();
  if (!url) return '';
  if (url.startsWith('//')) url = `https:${url}`;
  if (url.startsWith('http:')) url = url.replace('http:', 'https:');
  return url;
};

// Detect if current product detail is an Alibaba/Global product (from global search)
export const isAlibabaDetailContext = () => {
  try {
    const search = String(window.location.search || '').toLowerCase();
    // Product detail opened from global search uses ali=1
    return search.includes('ali=1');
  } catch {
    return false;
  }
};

// Append a size token before image extension (e.g., image.80x80.jpg)
// Skip for AWS S3/CloudFront URLs as they don't support this pattern
export const appendSizeToImageUrl = (url, size = '80x80') => {
  if (!url || typeof url !== 'string') return url;

  // Skip size appending for AWS URLs (S3/CloudFront) - they don't support this pattern
  if (url.includes('amazonaws.com') || url.includes('s3.') || url.includes('cloudfront.net') || url.includes('importerr-pimages')) {
    return url;
  }

  // Avoid duplicate sizing
  const alreadySized = new RegExp(`\\.${size}\\.(?:jpg|jpeg|png)(?:\\?.*)?$`, 'i');
  if (alreadySized.test(url)) return url;
  return url.replace(/\.(jpg|jpeg|png)(\?.*)?$/i, (_, ext, query = '') => `.${size}.${ext}${query}`);
};

// Prepare description HTML: remove images with href links, keep all other content
export const prepareDescriptionHtml = html => {
  if (!html || typeof html !== 'string') return '';
  let safe = String(html)
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

  // Remove images that are wrapped in anchor tags (have href links)
  safe = safe
    // Remove <a href="..."><img ...></a> patterns
    .replace(/<a[^>]*href[^>]*>[\s\S]*?<img[^>]*>[\s\S]*?<\/a>/gi, '')
    // Remove <a href="...">...</a> that contain images
    .replace(/<a[^>]*href[^>]*>[\s\S]*?<\/a>/gi, match => {
      // If the anchor tag contains an img tag, remove the entire anchor
      if (/<img[^>]*>/i.test(match)) {
        return '';
      }
      return match; // Keep anchor tags that don't contain images
    });

  // Remove ONLY RMB price text (keep all other description content)
  safe = safe
    // Remove RMB prices (¥ symbol followed by numbers)
    .replace(/¥\s*\d+[\d,.]*(?:\.\d+)?/gi, '')
    // Remove Chinese currency (元 followed by numbers)
    .replace(/元\s*\d+[\d,.]*(?:\.\d+)?/gi, '')
    // Remove RMB text followed by numbers
    .replace(/RMB\s*\d+[\d,.]*(?:\.\d+)?/gi, '')
    // Remove price ranges with RMB
    .replace(/¥\s*\d+[\d,.]*(?:\.\d+)?\s*-\s*¥\s*\d+[\d,.]*(?:\.\d+)?/gi, '');

  // Remove any HTML tag that contains RESIZE (with or without dot)
  safe = safe.replace(/<[^>]*RESIZE[^>]*>/gi, '');

  // Process remaining images to ensure proper formatting (KEEP ALL NON-LINKED IMAGES)
  safe = safe.replace(/<img([^>]*)>/gi, (_m, attrs) => {
    const getAttr = name => {
      const re = new RegExp(name + '\\s*=\\s*("([^"]*)"|\'([^\']*)\'|([^\s>]+))', 'i');
      const match = attrs.match(re);
      return match ? match[2] || match[3] || match[4] || '' : '';
    };

    let src = getAttr('src') || getAttr('data-src') || getAttr('data-original') || getAttr('data-lazy-src') || getAttr('data-image') || getAttr('data-url');
    src = sanitizeUrl(src);
    const alt = getAttr('alt') || '';

    if (!src) return '';

    return `<img src="${src}" style="max-width:100%;height:auto;display:block;margin:8px 0;" referrerpolicy="no-referrer" crossorigin="anonymous" alt="${alt}" />`;
  });

  return safe;
};

// Show toast notification
export const showToast = (msg = 'Copied') => {
  const id = '_copy_toast';
  const old = document.getElementById(id);
  if (old) old.remove();
  const el = document.createElement('div');
  el.id = id;
  el.textContent = msg;
  Object.assign(el.style, {
    position: 'fixed',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.85)',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    zIndex: 2147483647,
    opacity: '0',
    transition: 'opacity 150ms ease',
    pointerEvents: 'none',
  });
  document.body.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = '1';
  });
  setTimeout(() => {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 180);
  }, 1100);
};

// Copy text to clipboard
export const copyToClipboard = text => {
  const val = String(text || '');
  if (!val) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText(val)
      .then(() => showToast('✓ Copied'))
      .catch(() => {
        const ta = document.createElement('textarea');
        ta.value = val;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
          document.execCommand('copy');
          showToast('✓ Copied');
        } catch {
          showToast('Copy failed');
        } finally {
          ta.remove();
        }
      });
  } else {
    const ta = document.createElement('textarea');
    ta.value = val;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand('copy');
      showToast('✓ Copied');
    } catch {
      showToast('Copy failed');
    } finally {
      ta.remove();
    }
  }
};

/**
 * Chargeable weight per unit for pricing (PDP, cart, admin edit order).
 * Same rules as PDP: max(dead, volumetric) with DEFAULT_DW / VOLUME_FACTOR; buffer is applied in backend.
 * @returns {object} spread of variant + chargeableWeight
 */
export const calculateVariantChargeableWeight = variant => {
  const rawWeight = Number(variant?.weight) || 0;
  const rawLength = Number(variant?.length) || 0;
  const rawHeight = Number(variant?.height) || 0;
  const rawWidth = Number(variant?.width) || 0;

  const hasDimensions = rawLength > 0 && rawHeight > 0 && rawWidth > 0;
  const hasWeight = rawWeight > 0;

  const defaultWeight = PRICE_LOGIC.DEFAULT_DW || 0.5;
  const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR || 5000;

  const volumetricWeight = hasDimensions ? (rawLength * rawWidth * rawHeight) / volumeFactor : 0;

  let chargeableWeight;
  let effectiveVolumetricWeight = volumetricWeight;

  if (!hasDimensions && !hasWeight) {
    chargeableWeight = defaultWeight;
  } else if (hasWeight && !hasDimensions) {
    effectiveVolumetricWeight = defaultWeight;
    chargeableWeight = Math.max(rawWeight, effectiveVolumetricWeight);
  } else if (hasDimensions && !hasWeight) {
    chargeableWeight = Math.max(volumetricWeight, defaultWeight);
  } else {
    chargeableWeight = Math.max(rawWeight, volumetricWeight);
  }

  return {
    ...variant,
    chargeableWeight,
  };
};

// Memoized sorted ranges to avoid re-sorting on every call - O(1) after first sort
let _sortedRangesCache = null;
let _rangesCacheKey = null;

// Get applicable price based on quantity and price ranges - O(n) after first O(n log n) sort
export const getPriceForQuantity = (basePrice, totalQty, priceRanges, callerType) => {
  if (!priceRanges?.length || totalQty === 0) return null;

  // Cache key based on ranges array reference
  const currentKey = priceRanges;
  if (_rangesCacheKey !== currentKey) {
    // Sort descending by startQuantity (highest first)
    _sortedRangesCache = [...priceRanges].sort((a, b) => b.startQuantity - a.startQuantity);
    _rangesCacheKey = currentKey;
  }

  // Find the highest range where qty >= startQuantity
  // Since sorted descending, first match is the correct range
  return _sortedRangesCache.find(range => totalQty >= range.startQuantity) || priceRanges[0];
  // const applicableRange = _sortedRangesCache.find(range => totalQty >= range.startQuantity);
  // return applicableRange && callerType === 'ordersummary' ? parseFloat(applicableRange.price) : parseFloat(applicableRange?.FBPprice || 0);
};

// Calculate total quantity across all selected variants
export const getTotalSelectedQuantity = selectedVariants => Object.values(selectedVariants).reduce((sum, v) => sum + (v.quantity || 0), 0);

// Get effective price (range price if applicable, otherwise base price)
export const getEffectivePrice = (basePrice, totalQty, priceRanges) => {
  const rangePrice = getPriceForQuantity(basePrice, totalQty, priceRanges);
  return rangePrice || basePrice;
};

// Extract images from description HTML
export const extractImagesFromDescription = html => {
  if (!html) return [];
  const imgRegex = /<img[^>]+src="([^"]+)"/g;
  const images = [];
  let match;
  while ((match = imgRegex.exec(html)) !== null) {
    // Exclude images with RESIZE in the URL
    if (!match[1].includes('RESIZE')) {
      images.push(match[1]);
    }
  }
  return images;
};

/** productShippingInfo nested under DB snapshot globalApiResponse (flat or result.result) */
const getProductShippingInfoFromGlobalResponse = product => {
  const g = product?.globalApiResponse;
  if (!g || typeof g !== 'object') return null;
  if (g.productShippingInfo) return g.productShippingInfo;
  const inner = g.result?.result || g.result;
  if (inner && typeof inner === 'object' && inner.productShippingInfo) return inner.productShippingInfo;
  return null;
};

/**
 * Resolve productShippingInfo for PDP: API uses top-level/detailed_info; DB also uses globalApiResponse.
 */
export const getProductShippingInfoForPdp = product =>
  product?.detailed_info?.productShippingInfo ||
  product?.productShippingInfo ||
  getProductShippingInfoFromGlobalResponse(product) ||
  null;

/**
 * Fill missing sku/variant fields from parent productShippingInfo (independently):
 * - weight from parent when variant weight is 0
 * - L×W×H from parent when variant dimensions are incomplete (any of L/W/H is 0), so tiny SKU weight + zero dims still get parent box size
 */
export const applyParentLevelShippingPhysicals = (rawWeight, rawLength, rawHeight, rawWidth, parent) => {
  let rw = Number(rawWeight) || 0;
  let rl = Number(rawLength) || 0;
  let rh = Number(rawHeight) || 0;
  let rwi = Number(rawWidth) || 0;
  if (!parent || typeof parent !== 'object') {
    return { rawWeight: rw, rawLength: rl, rawHeight: rh, rawWidth: rwi };
  }
  const pw = Number(parent.weight);
  const pl = Number(parent.length);
  const pwd = Number(parent.width);
  const ph = Number(parent.height);
  const parentDimsOk = Number.isFinite(pl) && pl > 0 && Number.isFinite(pwd) && pwd > 0 && Number.isFinite(ph) && ph > 0;
  const variantDimsOk = rl > 0 && rwi > 0 && rh > 0;
  if (rw <= 0 && Number.isFinite(pw) && pw > 0) rw = pw;
  if (!variantDimsOk && parentDimsOk) {
    rl = pl;
    rwi = pwd;
    rh = ph;
  }
  return { rawWeight: rw, rawLength: rl, rawHeight: rh, rawWidth: rwi };
};

/** API: parent on product.productShippingInfo; DB: also globalApiResponse.productShippingInfo */
export const enrichVariantPhysicalsFromProductShipping = (product, rawWeight, rawLength, rawHeight, rawWidth) => {
  let a = applyParentLevelShippingPhysicals(rawWeight, rawLength, rawHeight, rawWidth, product?.detailed_info?.productShippingInfo || product?.productShippingInfo);
  a = applyParentLevelShippingPhysicals(a.rawWeight, a.rawLength, a.rawHeight, a.rawWidth, getProductShippingInfoFromGlobalResponse(product));
  return a;
};

// Calculate dropshipping final price for a variant (includes CN + duties/taxes)
// When dimensions or weight are missing/0, use default weight value
// Compare volumetric weight vs actual weight and use the maximum
// ALWAYS use weight and dimensions from detailed_info.productShippingInfo.skuShippingDetails by matching skuId
export const calculateDropshippingPrice = async (variant, product, signal) => {
  try {
    const basePrice = parseFloat(variant.ap || variant.cp || variant?.fenxiaoPriceInfo?.onePiecePrice || variant?.consignPrice || variant?.fenxiaoPriceInfo?.offerPrice || 0);
    if (!basePrice) return null;

    const offerId = product.offerId || product.sku;
    if (!offerId) return null;

    // COMMENTED: Freight API call - now using algorithm's local_shipping_rmb instead
    // const freightResult = await ProductsApiService.getLocalShippingCost(parseInt(offerId), [{ skuId: variant.skuId, quantity: 1 }], signal);
    // if (signal.aborted) return null;
    // const shippingCost = freightResult.success ? freightResult.shippingCost : null;

    // LS will be calculated from algorithm's local_shipping_rmb field
    const shippingCost = 0;

    // ALWAYS get weight and dimensions from detailed_info.productShippingInfo.skuShippingDetails by matching skuId
    let rawWeight = 0;
    let rawLength = 0;
    let rawHeight = 0;
    let rawWidth = 0;

    // Check if product has detailed_info.productShippingInfo.skuShippingDetails
    let skuShippingDetails = product?.detailed_info?.productShippingInfo?.skuShippingDetails ||
                               product?.productShippingInfo?.skuShippingDetails ||
                               [];
    // ADDITIVE (DB): sku rows may only exist under globalApiResponse.productShippingInfo
    if (!Array.isArray(skuShippingDetails) || skuShippingDetails.length === 0) {
      const gShip = getProductShippingInfoFromGlobalResponse(product);
      if (gShip?.skuShippingDetails?.length) skuShippingDetails = gShip.skuShippingDetails;
    }

    // Find matching shipping detail by skuId
    const shippingDetail = skuShippingDetails.find(detail =>
      String(detail.skuId) === String(variant.skuId) ||
      detail.skuId === variant.skuId
    );

    if (shippingDetail) {
      // Use values from skuShippingDetails (priority source)
      rawWeight = shippingDetail.weight || 0;
      rawLength = shippingDetail.length || 0;
      rawHeight = shippingDetail.height || 0;
      rawWidth = shippingDetail.width || 0;
    } else {
      // Fallback to variant values if skuShippingDetails not found
      rawWeight = variant.weight || 0;
      rawLength = variant.length || 0;
      rawHeight = variant.height || 0;
      rawWidth = variant.width || 0;
    }

    // ADDITIVE: fill missing weight and/or incomplete L×W×H from product-level productShippingInfo (API: root; DB: globalApiResponse.productShippingInfo)
    const parentFromApi = product?.detailed_info?.productShippingInfo || product?.productShippingInfo || null;
    const parentFromGlobal = getProductShippingInfoFromGlobalResponse(product);
    let applied = applyParentLevelShippingPhysicals(rawWeight, rawLength, rawHeight, rawWidth, parentFromApi);
    rawWeight = applied.rawWeight;
    rawLength = applied.rawLength;
    rawHeight = applied.rawHeight;
    rawWidth = applied.rawWidth;
    applied = applyParentLevelShippingPhysicals(rawWeight, rawLength, rawHeight, rawWidth, parentFromGlobal);
    rawWeight = applied.rawWeight;
    rawLength = applied.rawLength;
    rawHeight = applied.rawHeight;
    rawWidth = applied.rawWidth;

    // Check if dimensions are complete (all non-zero)
    const hasDimensions = rawLength > 0 && rawHeight > 0 && rawWidth > 0;
    const hasWeight = rawWeight > 0;

    // Get default weight from PRICE_LOGIC
    const defaultWeight = PRICE_LOGIC.DEFAULT_DW || 0.5;
    const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR || 5000;

    // Calculate volumetric weight if dimensions are available
    const volumetricWeight = hasDimensions ? (rawLength * rawWidth * rawHeight) / volumeFactor : 0;

    // Determine the final weight to use (same logic as wholesale/backend):
    // 1. If both dimensions and weight are missing, use default weight
    // 2. If only weight is available (dimensions missing), compare actualWeight vs defaultWeight
    // 3. If only dimensions are available (weight missing), use max(volumetricWeight, defaultWeight)
    // 4. If both are available, use max(actualWeight, volumetricWeight)
    let finalWeight;

    if (!hasDimensions && !hasWeight) {
      finalWeight = defaultWeight;
    } else if (hasWeight && !hasDimensions) {
      // Only weight available - compare with default weight
      finalWeight = Math.max(rawWeight, defaultWeight);
    } else if (hasDimensions && !hasWeight) {
      finalWeight = Math.max(volumetricWeight, defaultWeight);
    } else {
      finalWeight = Math.max(rawWeight, volumetricWeight);
    }

    // Calculate pricing breakdown
    const calculationData = {
      ap: basePrice,
      ls: shippingCost,
      hsn: product.hsn || null,
      hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
      dw: finalWeight,
      vw: volumetricWeight,
      categoryId: product?.categoryId || '',
      sku: String(offerId || variant.sku || variant.skuId || ''),
      topCategoryId: product?.topCategoryId || '',
      secondCategoryId: product?.secondCategoryId || '',
      thirdCategoryId: product?.thirdCategoryId || '',
      productShippingInfo: product?.productShippingInfo || {}
    };

    // Calculate pricing algorithm via service
    const data = await ProductsApiService.calculatePricingAlgorithm(calculationData);

    if (signal.aborted) return null;

    if (data.status.code === 200) {
      const breakdown = data.data.breakdown;
      const finalPrice = breakdown.FBP;
      const LS = breakdown.FBP;
      return { finalPrice, breakdown, localShipping: LS, ls: calculationData.ls };
    }

    return null;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error calculating dropshipping price:', error);
    }
    return null;
  }
};

// Generic to Calculate FBP (final buyer price) (includes CN + duties/taxes)
export const calculateFBP = async (variants = [], offerId, categoryId = '', hsn = null, hsnTaxPercent = null, signal = null, product = null) => {
  try {
    let totalBasePrice = 0;
    let totalWeight = 0;
    let totalChargeableWeight = 0;
    let totalVolume = 0;
    let totalQuantity = 0;
    variants = variants.reduce((acc, variant) => {
      acc.push({ skuId: variant.skuId || offerId, quantity: variant.quantity || 1 });
      // For dropshipping: use ap (onePiecePrice) first, then fallback to other prices
      // ap is set from onePiecePrice in applyMarkupToVariantPrices
      totalBasePrice += parseFloat(variant.ap || variant?.fenxiaoPriceInfo?.onePiecePrice || variant?.consignPrice || variant.cp || variant?.fenxiaoPriceInfo?.offerPrice || 0);
      totalWeight += (variant.weight || 0) * (variant.quantity || 1);
      // Use chargeableWeight for pricing if available, otherwise use weight
      totalChargeableWeight += (variant.chargeableWeight || variant.weight || 0) * (variant.quantity || 1);
      totalQuantity += variant.quantity;
      totalVolume += variant.height && variant.width && variant.length ? variant.height * variant.width * variant.length * (variant.quantity || 1) : 0;
      return acc;
    }, []);

    if (!offerId || !totalBasePrice) return null;

    // COMMENTED: Freight API call - now using algorithm's local_shipping_rmb instead
    // const freightResult = await ProductsApiService.getLocalShippingCost(parseInt(offerId), variants, signal);
    // if (signal?.aborted) return null;
    // const shippingCost = freightResult.success ? freightResult.shippingCost : null;

    // LS will be calculated from algorithm's local_shipping_rmb field
    const shippingCost = 0;

    // Calculate pricing breakdown - use chargeableWeight for pricing
    const calculationData = {
      ap: totalBasePrice,
      ls: shippingCost,
      hsn: hsn,
      hsnTaxPercent: hsnTaxPercent || PRICE_LOGIC.HSNTaxPercent,
      dw: totalChargeableWeight || PRICE_LOGIC.DEFAULT_DW,
      vw: totalVolume / PRICE_LOGIC.VOLUME_FACTOR,
      categoryId: categoryId,
      sku: String(offerId || ''),
      topCategoryId: product?.topCategoryId || '',
      secondCategoryId: product?.secondCategoryId || '',
      thirdCategoryId: product?.thirdCategoryId || '',
      productShippingInfo: product?.productShippingInfo || {},
    };

    // Calculate pricing algorithm via service
    const data = await ProductsApiService.calculatePricingAlgorithm(calculationData);

    if (signal?.aborted) return null;

    if (data.status.code === 200) {
      const breakdown = data.data.breakdown;
      const finalPrice = breakdown.FBP;
      const localShipping = breakdown.LS;
      return { finalPrice, breakdown, localShipping, ls: calculationData.ls };
    }

    return null;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error calculating dropshipping price:', error);
    }
    return null;
  }
};

export const makeWholesaleStartQuantity = (priceRanges, WholesaleInitialQuantity = 2) => {
  if (priceRanges.length && priceRanges[0].startQuantity === 1) priceRanges[0].startQuantity = WholesaleInitialQuantity;
};
